import * as React from "react";
import { motion, useInView } from "motion/react";
import { cn } from "~/lib/utils";
import { useContainerSize } from "~/lib/use-container-size";

export const AUTO_ADVANCE_SECONDS = 5;

// Autoplay starts once `sectionRef` scrolls into view, advances every `seconds`,
// and stops for good the moment the user selects an item themselves.
export function useAutoAdvance(
  count: number,
  sectionRef: React.RefObject<Element | null>,
  seconds: number = AUTO_ADVANCE_SECONDS,
) {
  const [activeIndex, setActiveIndex] = React.useState<number | null>(null);
  const [userHasInteracted, setUserHasInteracted] = React.useState(false);
  const isInView = useInView(sectionRef, { once: true, amount: 0.3 });

  // The first item goes active on scroll-in, derived at render time.
  const displayIndex = activeIndex ?? (isInView ? 0 : null);

  React.useEffect(() => {
    if (!isInView || userHasInteracted) return;
    const interval = setInterval(() => {
      setActiveIndex((current) => ((current ?? 0) + 1) % count);
    }, seconds * 1000);
    return () => clearInterval(interval);
  }, [isInView, userHasInteracted, count, seconds]);

  const select = React.useCallback((index: number) => {
    setActiveIndex(index);
    setUserHasInteracted(true);
  }, []);

  return { displayIndex, userHasInteracted, select };
}

interface AutoAdvanceTimerProps {
  isActive: boolean;
  userHasInteracted: boolean;
  /** Track position, size and color; the component only adds the opacity toggle. */
  className: string;
  fillClassName: string;
  /** Fill height while not growing, usually the track width so it reads as a dot. */
  restingFillHeight: string;
  seconds?: number;
}

// Hidden unless active. During autoplay the fill grows over the full interval,
// reaching the bottom as the next item activates; after a click it snaps full.
export function AutoAdvanceTimer({
  isActive,
  userHasInteracted,
  className,
  fillClassName,
  restingFillHeight,
  seconds = AUTO_ADVANCE_SECONDS,
}: AutoAdvanceTimerProps) {
  return (
    <div className={cn(className, isActive ? "opacity-100" : "opacity-0")}>
      <div
        className={fillClassName}
        style={{
          height: isActive ? "100%" : restingFillHeight,
          transition: !isActive ? "none" : userHasInteracted ? "height 0.3s ease" : `height linear ${seconds}s`,
        }}
      />
    </div>
  );
}

interface AutoAdvanceCollapseProps {
  isActive: boolean;
  children: React.ReactNode;
  id?: string;
  className?: string;
  /** Set when collapsed content holds focusable elements, so Tab skips them. */
  inert?: boolean;
}

// Animates the body's measured height; grid 0fr/1fr snapped instead of easing for
// multi-line wrapped text.
export function AutoAdvanceCollapse({ isActive, children, id, className, inert }: AutoAdvanceCollapseProps) {
  const [bodyEl, setBodyEl] = React.useState<HTMLDivElement | null>(null);
  const { height } = useContainerSize(bodyEl);

  return (
    <motion.div
      id={id}
      inert={inert}
      className={cn("overflow-hidden", className)}
      initial={false}
      animate={{ height: isActive ? height : 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      <div ref={setBodyEl}>{children}</div>
    </motion.div>
  );
}
